import { faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useComposedRefs } from '@reach/utils';
import { AnimatePresence, motion } from 'motion/react';
import { useRef } from 'react';

import styles from './SearchBar.module.scss';

interface SearchBarProps {
  value: string;
  placeholder?: string;
  contained?: boolean;
  realtime?: boolean;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  showClear?: boolean;
}

const SearchBar = ({
  ref: forwardedRef,
  value,
  placeholder = '',
  realtime = true,
  onSearch,
  onChange,
  contained = true,
  showClear = false,
}: SearchBarProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useComposedRefs(inputRef, forwardedRef);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;

    onChange(newValue);
    if (realtime) {
      onSearch?.(newValue);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onSearch?.(value);
    }
  }

  return (
    <div className={styles['search-bar']} data-contained={contained} data-show-clear={showClear}>
      <input
        type="text"
        className={styles['search-input']}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        ref={ref}
        onKeyDown={handleKeyDown}
      />
      <FontAwesomeIcon icon={faSearch} className={styles['search-icon']} />
      <AnimatePresence>
        {showClear && !!value && (
          <motion.button
            className={styles['clear-btn']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
            onClick={() => {
              onChange('');
              inputRef.current?.focus();
            }}
          >
            <FontAwesomeIcon icon={faTimes} className={styles['clear-icon']} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

SearchBar.displayName = 'SearchBar';

export default SearchBar;
